import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message, Button } from 'antd';
import type { Manager, Club } from '@/types/models';
import type { ApiResponse } from '@/types/api';
import { apiService } from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';

interface CreateManagerModalProps {
  visible: boolean;
  manager?: Manager | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const CreateManagerModal: React.FC<CreateManagerModalProps> = ({
  visible,
  manager,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(false);
  const isEditing = !!manager;

  // Загрузка списка клубов
  const fetchClubs = async () => {
    try {
      setLoading(true);
      const { data } = await apiService.get<{ data: Club[], status: number }>(API_ENDPOINTS.CLUBS.BASE);
      if (data?.data) {
        setClubs(data.data);
      }
    } catch (error) {
      logger.error('Error fetching clubs:', error);
      message.error('Ошибка при загрузке списка клубов');
    } finally {
      setLoading(false);
    }
  };

  // Загружаем список клубов при открытии модального окна
  useEffect(() => {
    if (visible) {
      fetchClubs();
      if (manager) {

        // Get the clubId from clubManagers if it exists
        const clubId = manager.clubManagers && manager.clubManagers.length > 0
          ? manager.clubManagers[0].clubId
          : undefined;


        form.setFieldsValue({
          firstName: manager.firstName,
          lastName: manager.lastName,
          middleName: manager.middleName,
          email: manager.email,
          role: manager.role || 'manager',
          status: manager.status || 'active',
          clubId: clubId
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          role: 'manager',
          status: 'active'
        });
      }
    }
  }, [visible, manager, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (isEditing && manager) {
        // Обновление существующего менеджера
        await apiService.put(`${API_ENDPOINTS.MANAGERS.BASE}/${manager.id}`, {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          role: values.role,
          status: values.status,
          clubId: values.clubId // Теперь обновляем clubId напрямую
        });

        // Связь с клубом теперь обрабатывается через прямое обновление clubId в модели User

        message.success('Менеджер успешно обновлен');
      } else {
        // Создание нового менеджера
        await apiService.post<ApiResponse<{id: number}>>(
          API_ENDPOINTS.MANAGERS.BASE, {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          role: values.role,
          status: values.status,
          password: values.password, // Пароль нужен только при создании
          clubId: values.clubId // Теперь передаем clubId напрямую
        });

        message.success('Менеджер успешно создан');
      }

      form.resetFields();
      onSuccess();
    } catch (error: any) {
      logger.error('Error submitting manager form:', error);

      // Проверяем наличие сообщения об ошибке в ответе API
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.message && error.message.includes('already associated with this club')) {
        message.error('Менеджер уже связан с этим клубом');
      } else {
        message.error('Ошибка при сохранении данных менеджера');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEditing ? 'Редактировать менеджера' : 'Добавить менеджера'}
      open={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ role: 'manager', status: 'active' }}
      >
        <Form.Item
          name="lastName"
          label="Фамилия"
          rules={[{ required: true, message: 'Введите фамилию' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="firstName"
          label="Имя"
          rules={[{ required: true, message: 'Введите имя' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="middleName"
          label="Отчество"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Введите email' },
            { type: 'email', message: 'Введите корректный email' }
          ]}
        >
          <Input />
        </Form.Item>

        {!isEditing && (
          <Form.Item
            name="password"
            label="Пароль"
            rules={[
              { required: true, message: 'Введите пароль' },
              { min: 6, message: 'Пароль должен содержать минимум 6 символов' }
            ]}
          >
            <Input.Password />
          </Form.Item>
        )}

        {/* Поле organization удалено, теперь организация определяется через клуб */}

        <Form.Item
          name="role"
          label="Роль"
          rules={[{ required: true, message: 'Выберите роль' }]}
        >
          <Select>
            <Select.Option value="manager">Менеджер</Select.Option>
            <Select.Option value="head_manager">Главный менеджер</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="status"
          label="Статус"
          rules={[{ required: true, message: 'Выберите статус' }]}
        >
          <Select>
            <Select.Option value="active">Активен</Select.Option>
            <Select.Option value="inactive">Неактивен</Select.Option>
            <Select.Option value="pending">Ожидает подтверждения</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="clubId"
          label="Клуб"
        >
          <Select
            placeholder="Выберите клуб"
            loading={loading}
            allowClear
          >
            {clubs.map(club => (
              <Select.Option key={club.id} value={club.id}>{club.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item className="mb-0 text-right">
          <Button onClick={onCancel} style={{ marginRight: 8 }}>
            Отмена
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isEditing ? 'Сохранить' : 'Создать'}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateManagerModal;
